import React, { useState, useEffect } from 'react';
import {
    PlusIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    SparklesIcon,
    CubeIcon,
    SwatchIcon,
    MapIcon,
    ChartBarIcon,
    LightBulbIcon
} from '@heroicons/react/24/outline';
import { authorizedFetch } from '../utils/api';
import CurationIntelligence from '../components/CurationIntelligence';
import VisualMerchandisingDesigner from '../components/VisualMerchandisingDesigner';

const ConceptualCuration = () => {
    const [activeTab, setActiveTab] = useState('themes');
    const [themes, setThemes] = useState([]);
    const [collections, setCollections] = useState([]);
    const [storeLayouts, setStoreLayouts] = useState([]);
    const [curationProjects, setCurationProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTheme, setSelectedTheme] = useState(null);
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [showLayoutModal, setShowLayoutModal] = useState(false);
    const [newTheme, setNewTheme] = useState({
        name: '',
        concept: '',
        aesthetic_direction: '',
        target_demographic: '',
        color_palette: [],
        silhouette_focus: '',
        material_emphasis: '',
        cultural_references: '',
        artistic_inspiration: '',
        commercial_viability: 5,
        avant_garde_level: 5
    });

    const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';

    useEffect(() => {
        fetchCurationData();
    }, []);

    const fetchCurationData = async () => {
        setLoading(true);
        try {
            const [themesRes, collectionsRes, layoutsRes, projectsRes] = await Promise.all([
                authorizedFetch(`${backendUrl}/conceptual-curation/themes`),
                authorizedFetch(`${backendUrl}/conceptual-curation/collections`),
                authorizedFetch(`${backendUrl}/conceptual-curation/store-layouts`),
                authorizedFetch(`${backendUrl}/conceptual-curation/projects`)
            ]);

            if (themesRes.ok) setThemes(await themesRes.json());
            if (collectionsRes.ok) setCollections(await collectionsRes.json());
            if (layoutsRes.ok) setStoreLayouts(await layoutsRes.json());
            if (projectsRes.ok) setCurationProjects(await projectsRes.json());
        } catch (error) {
            console.error('Error fetching curation data:', error);
        } finally {
            setLoading(false);
        }
    };

    const createTheme = async () => {
        try {
            const response = await authorizedFetch(`${backendUrl}/conceptual-curation/themes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newTheme)
            });

            if (response.ok) {
                const theme = await response.json();
                setThemes([...themes, theme]);
                setShowThemeModal(false);
                setNewTheme({
                    name: '',
                    concept: '',
                    aesthetic_direction: '',
                    target_demographic: '',
                    color_palette: [],
                    silhouette_focus: '',
                    material_emphasis: '',
                    cultural_references: '',
                    artistic_inspiration: '',
                    commercial_viability: 5,
                    avant_garde_level: 5
                });
            }
        } catch (error) {
            console.error('Error creating theme:', error);
        }
    };

    const ThemeCard = ({ theme }) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {theme.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                        {theme.concept}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setSelectedTheme(theme)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    >
                        <EyeIcon className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
                        <PencilIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <SwatchIcon className="w-4 h-4 text-purple-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                        {theme.aesthetic_direction}
                    </span>
                </div>

                <div className="flex items-center gap-2">
                    <CubeIcon className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                        {theme.silhouette_focus}
                    </span>
                </div>

                <div className="flex justify-between items-center pt-3 border-t dark:border-gray-600">
                    <div className="flex items-center gap-4">
                        <div className="text-center">
                            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                {theme.commercial_viability}/10
                            </div>
                            <div className="text-xs text-gray-500">Commercial</div>
                        </div>
                        <div className="text-center">
                            <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                {theme.avant_garde_level}/10
                            </div>
                            <div className="text-xs text-gray-500">Avant-garde</div>
                        </div>
                    </div>

                    <div className="flex gap-1">
                        {theme.color_palette?.slice(0, 4).map((color, idx) => (
                            <div
                                key={idx}
                                className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                                style={{ backgroundColor: color }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const StoreLayoutCard = ({ layout }) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {layout.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {layout.store_type} • {layout.square_footage} sq ft
                    </p>
                </div>
                <div className="flex gap-2">
                    <button className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
                        <MapIcon className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
                        <PencilIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Zones:</span>
                    <span className="text-gray-900 dark:text-white">{layout.zones?.length || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Capacity:</span>
                    <span className="text-gray-900 dark:text-white">{layout.piece_capacity} pieces</span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Flow Score:</span>
                    <span className="text-gray-900 dark:text-white">{layout.flow_efficiency}/10</span>
                </div>
            </div>

            <div className="flex flex-wrap gap-1">
                {layout.zones?.slice(0, 3).map((zone, idx) => (
                    <span
                        key={idx}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs rounded"
                    >
                        {zone.name}
                    </span>
                ))}
                {layout.zones?.length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs rounded">
                        +{layout.zones.length - 3} more
                    </span>
                )}
            </div>
        </div>
    );

    const CurationProjectCard = ({ project }) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {project.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {project.season} {project.year} • {project.target_store}
                    </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${project.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                    project.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                    {project.status.replace('_', ' ')}
                </span>
            </div>

            <div className="space-y-3">
                <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Selected Pieces:</span>
                    <span className="text-gray-900 dark:text-white">{project.selected_pieces?.length || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Budget Utilized:</span>
                    <span className="text-gray-900 dark:text-white">
                        {project.budget_utilized}% of ${project.total_budget?.toLocaleString()}
                    </span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Coherence Score:</span>
                    <span className="text-gray-900 dark:text-white">{project.thematic_coherence}/10</span>
                </div>
            </div>

            <div className="mt-4 pt-3 border-t dark:border-gray-600">
                <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                        Updated {new Date(project.updated_at).toLocaleDateString()}
                    </div>
                    <button className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors">
                        Continue Curation
                    </button>
                </div>
            </div>
        </div>
    );

    const ThemeModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            Create Conceptual Theme
                        </h2>
                        <button
                            onClick={() => setShowThemeModal(false)}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Theme Name
                                </label>
                                <input
                                    type="text"
                                    value={newTheme.name}
                                    onChange={(e) => setNewTheme({ ...newTheme, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="e.g., Deconstructed Minimalism"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Core Concept
                                </label>
                                <textarea
                                    value={newTheme.concept}
                                    onChange={(e) => setNewTheme({ ...newTheme, concept: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Describe the philosophical foundation of this theme..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Aesthetic Direction
                                </label>
                                <select
                                    value={newTheme.aesthetic_direction}
                                    onChange={(e) => setNewTheme({ ...newTheme, aesthetic_direction: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="">Select Direction</option>
                                    <option value="Avant-garde Minimalism">Avant-garde Minimalism</option>
                                    <option value="Deconstructed Classicism">Deconstructed Classicism</option>
                                    <option value="Conceptual Streetwear">Conceptual Streetwear</option>
                                    <option value="Neo-Gothic Romance">Neo-Gothic Romance</option>
                                    <option value="Industrial Femininity">Industrial Femininity</option>
                                    <option value="Architectural Silhouettes">Architectural Silhouettes</option>
                                    <option value="Surrealist Functionality">Surrealist Functionality</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Target Demographic
                                </label>
                                <select
                                    value={newTheme.target_demographic}
                                    onChange={(e) => setNewTheme({ ...newTheme, target_demographic: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="">Select Demographic</option>
                                    <option value="Avant-garde Collectors">Avant-garde Collectors</option>
                                    <option value="Fashion Intellectuals">Fashion Intellectuals</option>
                                    <option value="Creative Professionals">Creative Professionals</option>
                                    <option value="Art World Insiders">Art World Insiders</option>
                                    <option value="Conceptual Fashion Enthusiasts">Conceptual Fashion Enthusiasts</option>
                                </select>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Silhouette Focus
                                </label>
                                <input
                                    type="text"
                                    value={newTheme.silhouette_focus}
                                    onChange={(e) => setNewTheme({ ...newTheme, silhouette_focus: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="e.g., Asymmetrical volumes, Geometric cuts"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Material Emphasis
                                </label>
                                <input
                                    type="text"
                                    value={newTheme.material_emphasis}
                                    onChange={(e) => setNewTheme({ ...newTheme, material_emphasis: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="e.g., Raw edges, Technical fabrics, Unconventional textures"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Cultural References
                                </label>
                                <textarea
                                    value={newTheme.cultural_references}
                                    onChange={(e) => setNewTheme({ ...newTheme, cultural_references: e.target.value })}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Cultural, historical, or artistic references..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Artistic Inspiration
                                </label>
                                <textarea
                                    value={newTheme.artistic_inspiration}
                                    onChange={(e) => setNewTheme({ ...newTheme, artistic_inspiration: e.target.value })}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Artists, movements, or concepts that inspire this theme..."
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Commercial Viability (1-10)
                                    </label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={newTheme.commercial_viability}
                                        onChange={(e) => setNewTheme({ ...newTheme, commercial_viability: parseInt(e.target.value) })}
                                        className="w-full"
                                    />
                                    <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                                        {newTheme.commercial_viability}/10
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Avant-garde Level (1-10)
                                    </label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={newTheme.avant_garde_level}
                                        onChange={(e) => setNewTheme({ ...newTheme, avant_garde_level: parseInt(e.target.value) })}
                                        className="w-full"
                                    />
                                    <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                                        {newTheme.avant_garde_level}/10
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6 pt-6 border-t dark:border-gray-600">
                        <button
                            onClick={() => setShowThemeModal(false)}
                            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={createTheme}
                            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                        >
                            Create Theme
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            Conceptual Curation & Store Organization
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Professional tools for avant-garde fashion curation and boutique organization
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowThemeModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
                        >
                            <SparklesIcon className="w-4 h-4" />
                            New Theme
                        </button>
                        <button
                            onClick={() => setShowLayoutModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                        >
                            <MapIcon className="w-4 h-4" />
                            New Layout
                        </button>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    {[
                        { id: 'themes', label: 'Conceptual Themes', icon: SparklesIcon },
                        { id: 'curation', label: 'Active Curation', icon: LightBulbIcon },
                        { id: 'layouts', label: 'Store Layouts', icon: MapIcon },
                        { id: 'analytics', label: 'Curation Analytics', icon: ChartBarIcon }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.id
                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'themes' && (
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Conceptual Themes ({themes.length})
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {themes.map((theme) => (
                            <ThemeCard key={theme.id} theme={theme} />
                        ))}

                        {themes.length === 0 && (
                            <div className="col-span-full text-center py-12">
                                <SparklesIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    No Conceptual Themes Yet
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-4">
                                    Create your first conceptual theme to begin avant-garde curation
                                </p>
                                <button
                                    onClick={() => setShowThemeModal(true)}
                                    className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
                                >
                                    Create First Theme
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'curation' && (
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Active Curation Projects ({curationProjects.length})
                        </h2>
                        <button className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors">
                            <PlusIcon className="w-4 h-4" />
                            New Project
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {curationProjects.map((project) => (
                            <CurationProjectCard key={project.id} project={project} />
                        ))}

                        {curationProjects.length === 0 && (
                            <div className="col-span-full text-center py-12">
                                <LightBulbIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    No Active Curation Projects
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-4">
                                    Start a new curation project to organize pieces for your boutique
                                </p>
                                <button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors">
                                    Start New Project
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'layouts' && (
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Store Layouts ({storeLayouts.length})
                        </h2>
                    </div>

                    {storeLayouts.length > 0 ? (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {storeLayouts.map((layout) => (
                                    <StoreLayoutCard key={layout.id} layout={layout} />
                                ))}
                            </div>

                            {/* Visual Merchandising Designer */}
                            <VisualMerchandisingDesigner
                                layout={storeLayouts[0]}
                                onLayoutUpdate={(updatedLayout) => {
                                    const updatedLayouts = storeLayouts.map(layout =>
                                        layout.id === updatedLayout.id ? updatedLayout : layout
                                    );
                                    setStoreLayouts(updatedLayouts);
                                }}
                            />
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <MapIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                No Store Layouts Configured
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                                Design your first store layout for optimal piece presentation
                            </p>
                            <button
                                onClick={() => setShowLayoutModal(true)}
                                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                            >
                                Create First Layout
                            </button>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'analytics' && (
                <CurationIntelligence
                    themes={themes}
                    projects={curationProjects}
                    onThemeRecommendation={(recommendation) => {
                        console.log('AI Recommendation:', recommendation);
                    }}
                />
            )}

            {/* Modals */}
            {showThemeModal && <ThemeModal />}

            {/* Theme Detail Modal */}
            {selectedTheme && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {selectedTheme.name}
                                </h2>
                                <button
                                    onClick={() => setSelectedTheme(null)}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        Core Concept
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        {selectedTheme.concept}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                            Aesthetic Direction
                                        </h4>
                                        <p className="text-gray-600 dark:text-gray-400">
                                            {selectedTheme.aesthetic_direction}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                            Target Demographic
                                        </h4>
                                        <p className="text-gray-600 dark:text-gray-400">
                                            {selectedTheme.target_demographic}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                            Silhouette Focus
                                        </h4>
                                        <p className="text-gray-600 dark:text-gray-400">
                                            {selectedTheme.silhouette_focus}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                            Material Emphasis
                                        </h4>
                                        <p className="text-gray-600 dark:text-gray-400">
                                            {selectedTheme.material_emphasis}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                        Cultural References
                                    </h4>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        {selectedTheme.cultural_references}
                                    </p>
                                </div>

                                <div>
                                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                        Artistic Inspiration
                                    </h4>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        {selectedTheme.artistic_inspiration}
                                    </p>
                                </div>

                                <div className="flex justify-between items-center pt-4 border-t dark:border-gray-600">
                                    <div className="flex items-center gap-6">
                                        <div className="text-center">
                                            <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                                {selectedTheme.commercial_viability}/10
                                            </div>
                                            <div className="text-sm text-gray-500">Commercial Viability</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                                                {selectedTheme.avant_garde_level}/10
                                            </div>
                                            <div className="text-sm text-gray-500">Avant-garde Level</div>
                                        </div>
                                    </div>

                                    <div className="flex gap-1">
                                        {selectedTheme.color_palette?.map((color, idx) => (
                                            <div
                                                key={idx}
                                                className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                                                style={{ backgroundColor: color }}
                                                title={color}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConceptualCuration;
